// Voice helpers with no browser dependencies: the voice list, markdown -> speakable text,
// and splitting long replies into chunks for the speech endpoint.

/** OpenAI's built-in voices (marin and cedar first: OpenAI's picks for quality). tts-1 and tts-1-hd only have the ones without `newer`. */
export const VOICES = [
  { id: 'marin', newer: true },
  { id: 'cedar', newer: true },
  { id: 'alloy' },
  { id: 'ash' },
  { id: 'ballad', newer: true },
  { id: 'coral' },
  { id: 'echo' },
  { id: 'fable' },
  { id: 'nova' },
  { id: 'onyx' },
  { id: 'sage' },
  { id: 'shimmer' },
  { id: 'verse', newer: true },
];

export const STT_MODELS = ['gpt-transcribe', 'gpt-4o-transcribe', 'gpt-4o-mini-transcribe', 'whisper-1'];
export const TTS_MODELS = ['gpt-4o-mini-tts', 'tts-1-hd', 'tts-1'];

/** Whether a TTS model offers a voice (the tts-1 family lacks the newer ones). */
export function voiceSupported(model, voice) {
  const v = VOICES.find((x) => x.id === voice);
  return !v?.newer || !/^tts-1/.test(model);
}

/**
 * Push-to-talk chords. On Windows, Chrome keeps Alt+Space for the window menu: pages and
 * extension shortcuts never receive it, so it is only offered for other systems.
 */
export const TALK_KEYS = {
  'ctrl+space': { label: 'Ctrl+Space', ctrl: true },
  'ctrl+shift+space': { label: 'Ctrl+Shift+Space', ctrl: true, shift: true },
  'alt+space': { label: 'Alt+Space', alt: true },
};

export const talkKey = (id) => TALK_KEYS[id] ?? TALK_KEYS['ctrl+space'];

/** True for the chord's Space press with exactly its modifiers. */
export function isTalkKey(e, id) {
  const k = talkKey(id);
  return e.code === 'Space' && e.ctrlKey === !!k.ctrl && e.altKey === !!k.alt && e.shiftKey === !!k.shift && !e.metaKey;
}

/** The keys (KeyboardEvent.key, and 'Space') that make up a chord. */
export function talkKeyNames(id) {
  const k = talkKey(id);
  return ['Space', k.ctrl && 'Control', k.alt && 'Alt', k.shift && 'Shift'].filter(Boolean);
}

function inline(s) {
  return s
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/__([^_]+)__/g, '$1')
    .replace(/(^|[^*\w])\*([^*\s][^*]*)\*(?!\w)/g, '$1$2')
    .replace(/https?:\/\/(?:www\.)?([^\s/)]+)[^\s)]*/g, '$1')
    .replace(/\s*(?:→|->)\s*/g, ' to ')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** A pause at the end of a list item or heading that has no punctuation of its own. */
const closed = (s) => (/[.!?:;,…]$/.test(s) ? s : `${s}.`);

/** Chat markdown as plain text for reading aloud: no markup, code blocks or URLs. */
export function speechText(markdown) {
  const out = [];
  let fenced = false;
  for (const raw of String(markdown ?? '').replace(/\r\n/g, '\n').split('\n')) {
    if (/^\s*```/.test(raw)) {
      fenced = !fenced;
      continue;
    }
    if (fenced) continue;
    if (/^\s*\|?[\s:|-]*-{3,}[\s:|-]*$/.test(raw)) continue; // rule or table separator
    let line = raw.replace(/^\s*>\s?/, '');
    const heading = /^#{1,6}\s+(.*)$/.exec(line);
    const item = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/.exec(line);
    if (/^\s*\|.*\|\s*$/.test(line)) {
      line = line
        .trim()
        .slice(1, -1)
        .split('|')
        .map((c) => inline(c))
        .filter(Boolean)
        .join(', ');
      out.push(line && closed(line));
    } else if (heading || item) {
      const t = inline((heading ?? item)[1]);
      out.push(t && closed(t));
    } else out.push(inline(line));
  }
  return out
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Split text into chunks for the speech endpoint, on sentence boundaries where possible.
 * The first chunk is short so the reply starts playing quickly.
 */
export function speechChunks(text, { first = 220, max = 900 } = {}) {
  const sentences = [];
  for (const para of String(text ?? '').split(/\n{2,}/)) {
    for (const line of para.split('\n')) {
      for (const s of line.split(/(?<=[.!?…])\s+(?=\S)/)) {
        let rest = s.trim();
        // a single sentence over the limit is cut at the last space before it
        while (rest.length > max) {
          const cut = rest.lastIndexOf(' ', max);
          const at = cut > max / 2 ? cut : max;
          sentences.push(rest.slice(0, at).trim());
          rest = rest.slice(at).trim();
        }
        if (rest) sentences.push(rest);
      }
    }
  }
  const chunks = [];
  let cur = '';
  for (const s of sentences) {
    const limit = chunks.length ? max : first;
    if (cur && cur.length + 1 + s.length > limit) {
      chunks.push(cur);
      cur = s;
    } else cur = cur ? `${cur} ${s}` : s;
  }
  if (cur) chunks.push(cur);
  return chunks;
}
